export const FULFILLMENT_QUEUE_GROUPS = Object.freeze([
  {
    key: 'TO_PREPARE',
    label: 'To Prepare',
    stages: ['NEW', 'PACKING', 'PACKED'],
    emptyTitle: 'No orders need preparation',
    emptyDescription: 'New paid orders and parcels still being packed will appear here.',
  },
  {
    key: 'READY_FOR_HANDOFF',
    label: 'Ready for Handoff',
    stages: ['READY'],
    emptyTitle: 'No parcels are waiting for handoff',
    emptyDescription: 'Packed parcels appear here after identity and readiness checks are complete.',
  },
  {
    key: 'HANDOFF_IN_PROGRESS',
    label: 'Handoff in Progress',
    stages: ['DISPATCHING'],
    emptyTitle: 'No rider handoffs are in progress',
    emptyDescription: 'Assigned pickups appear here after the rider starts the pickup journey.',
  },
  {
    key: 'NEEDS_ATTENTION',
    label: 'Needs Attention',
    stages: ['EXCEPTIONS'],
    emptyTitle: 'No fulfillment blockers',
    emptyDescription: 'Blocked readiness checks, dispatch holds, and failed shipments will appear here.',
  },
]);

const GROUP_BY_KEY = Object.fromEntries(FULFILLMENT_QUEUE_GROUPS.map(group => [group.key, group]));
const GROUP_BY_STAGE = Object.fromEntries(
  FULFILLMENT_QUEUE_GROUPS.flatMap(group => group.stages.map(stage => [stage, group.key])),
);

export function normalizeQueueGroup(value) {
  const normalized = String(value || '').trim().toUpperCase();
  return GROUP_BY_KEY[normalized]?.key || GROUP_BY_STAGE[normalized] || 'TO_PREPARE';
}

export function queueGroupDefinition(value) {
  return GROUP_BY_KEY[normalizeQueueGroup(value)];
}

export function queueStageParam(value) {
  return queueGroupDefinition(value).stages.join(',');
}

export function queueGroupCount(counts = {}, value) {
  return queueGroupDefinition(value).stages.reduce(
    (total, stage) => total + Number(counts?.[stage] || 0),
    0,
  );
}

export function queueGroupForItem(item = {}) {
  return normalizeQueueGroup(item.stage);
}

export function orderPresentation(item = {}) {
  const stage = String(item.stage || '').toUpperCase();
  // The server decides whether packing can start. An online payment that has not
  // settled is a wait, not work: showing it under "To prepare" invited staff to
  // start packing an order the server would refuse.
  if (item.nextAction === 'AWAIT_PAYMENT') {
    return { group: 'NEEDS_ATTENTION', label: 'Waiting for payment', tone: 'neutral' };
  }
  if (stage === 'NEW') return { group: 'TO_PREPARE', label: 'Start packing', tone: 'action' };
  if (stage === 'PACKING') {
    return item.checklist?.complete
      ? { group: 'TO_PREPARE', label: 'Confirm packed', tone: 'progress' }
      : { group: 'TO_PREPARE', label: 'Continue packing', tone: 'progress' };
  }
  if (stage === 'PACKED') {
    // Generate the label, then confirm it is attached; readiness follows from
    // that on its own. There is no readiness step to offer.
    if (item.nextAction === 'ISSUE_PARCEL_LABEL') return { group: 'TO_PREPARE', label: 'Generate label', tone: 'action' };
    if (item.nextAction === 'CONFIRM_LABEL_ATTACHED') return { group: 'TO_PREPARE', label: 'Confirm label attached', tone: 'action' };
    return { group: 'TO_PREPARE', label: 'Review parcel', tone: 'neutral' };
  }
  if (stage === 'READY') {
    if (item.delivery?.assignmentStatus === 'COMMITTED') {
      return { group: 'READY_FOR_HANDOFF', label: item.delivery?.riderName ? `Rider assigned: ${item.delivery.riderName}` : 'Rider assigned', tone: 'ready' };
    }
    // Automatic dispatch has tried and failed: that is something to fix, not wait for.
    if (item.delivery?.lastDispatch) {
      return { group: 'NEEDS_ATTENTION', label: 'Not dispatched: see why', tone: 'danger' };
    }
    return { group: 'READY_FOR_HANDOFF', label: 'Finding a rider', tone: 'ready' };
  }
  if (stage === 'DISPATCHING') {
    return { group: 'HANDOFF_IN_PROGRESS', label: item.delivery?.riderName ? `Pickup in progress: ${item.delivery.riderName}` : 'Pickup in progress', tone: 'progress' };
  }
  if (stage === 'EXCEPTIONS') {
    return { group: 'NEEDS_ATTENTION', label: item.dispatchHold?.label || item.blocker || 'Review blocker', tone: item.dispatchHold && !item.dispatchHold.next ? 'neutral' : 'danger' };
  }
  if (stage === 'DELIVERY' || stage === 'RIDER_APPROACHING') {
    return { group: null, label: 'In delivery', tone: 'neutral' };
  }
  return { group: null, label: 'Review order', tone: 'neutral' };
}

// The stage as a person reads it. The codes stay for filtering.
const STAGE_NAMES = Object.freeze({
  NEW: 'New order',
  PACKING: 'Being packed',
  PACKED: 'Packed',
  READY: 'Ready for a rider',
  DISPATCHING: 'Rider picking up',
  DELIVERY: 'Out for delivery',
  RIDER_APPROACHING: 'Out for delivery',
  EXCEPTIONS: 'Needs attention',
});

export function stageName(stage) {
  const code = String(stage || '').toUpperCase();
  if (!code) return 'Stage unavailable';
  if (STAGE_NAMES[code]) return STAGE_NAMES[code];
  const words = code.replaceAll('_', ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Where an order is, in one phrase: the parcel's latest fact once it has left
// the store, otherwise the packing stage. Fulfilment stays "Packed" after
// delivery, so showing both read as "Packed / Delivered" (APP-ORD-007).
const SHIPMENT_PROGRESS = Object.freeze({
  IN_TRANSIT: 'With the rider',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  FAILED: 'Delivery failed',
  RETURNED: 'Returned to the store',
});
const FULFILLMENT_PROGRESS = Object.freeze({
  PENDING: 'New order',
  PROCESSING: 'Being packed',
  PACKED: 'Packed',
  READY_FOR_DISPATCH: 'Ready for a rider',
  CANCELLED: 'Cancelled',
});

function readable(code) {
  const words = String(code || '').replaceAll('_', ' ').toLowerCase().trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

export function orderProgress(item = {}) {
  const shipment = String(item.shipmentStatus || '').toUpperCase();
  const fulfillment = String(item.sellerFulfillmentStatus || item.status || '').toUpperCase();
  if (fulfillment === 'CANCELLED') return { label: 'Cancelled', detail: null };
  if (SHIPMENT_PROGRESS[shipment]) return { label: SHIPMENT_PROGRESS[shipment], detail: null };
  return {
    label: FULFILLMENT_PROGRESS[fulfillment] || readable(fulfillment) || 'Status unavailable',
    detail: 'Not sent out yet',
  };
}

export function paymentLabel(method, status) {
  const code = String(method || '').toUpperCase();
  const cod = code === 'COD';
  const state = String(status || '').toUpperCase();
  const how = { COD: 'Cash on delivery', PAYMONGO_CHECKOUT: 'Online payment' }[code] || readable(method) || 'Payment';
  const where = {
    PAID: 'paid',
    PENDING: cod ? 'to collect' : 'awaiting payment',
    FAILED: 'payment failed',
    REFUNDED: 'refunded',
    CANCELLED: 'cancelled',
  }[state] || readable(state).toLowerCase();
  return where ? `${how} · ${where}` : how;
}

export function formatWaitingAge(seconds) {
  const minutes = Math.max(0, Math.floor(Number(seconds || 0) / 60));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function formatOrderMoney(value) {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' })
    .format(Number(value || 0));
}

export function formatOrderItems(items = []) {
  if (!Array.isArray(items) || items.length === 0) return 'Item details unavailable';
  return items.map(item => `${Number(item.quantity || 0)}× ${item.name || 'Item'}`).join(', ');
}
